import {
  AUDIO_PLAYBACK_ENDINGS,
  AUDIO_PLAYBACK_STATUSES,
  type AudioPlaybackCompletion,
  type AudioPlaybackEnding,
  type AudioPlaybackPort,
  type AudioPlaybackRequest,
  type AudioPlaybackStatus,
} from '@/shared-responder-v2-core';

/**
 * The slice of `HTMLAudioElement` the port drives, declared structurally so a
 * spec can hand the factory a fake element instead of a real one.
 */
export interface AudioPlaybackElement {
  src: string;
  currentTime: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: string, listener: () => void): void;
}

type PlaybackListener = (completion: AudioPlaybackCompletion) => void;

const AUDIO_ELEMENT_UNAVAILABLE_MESSAGE = 'Audio element unavailable';

function toFailureMessage(error: unknown): string | null {
  return error instanceof Error ? error.message : null;
}

function toStatusAfter(ending: AudioPlaybackEnding): AudioPlaybackStatus {
  return ending === AUDIO_PLAYBACK_ENDINGS.Failed
    ? AUDIO_PLAYBACK_STATUSES.Failed
    : AUDIO_PLAYBACK_STATUSES.Idle;
}

/**
 * One element for the whole session: a second `play` supersedes the first
 * rather than overlapping it, so the hands-free script can never stack two
 * assistant voices over each other.
 */
export function createBrowserAudioPlaybackPort(
  notify: () => void,
  audio: AudioPlaybackElement | null
): AudioPlaybackPort {
  const listeners = new Set<PlaybackListener>();

  let status: AudioPlaybackStatus = AUDIO_PLAYBACK_STATUSES.Idle;
  let currentPlaybackId: string | null = null;
  let resolveCurrent: PlaybackListener | null = null;
  let generation = 0;

  function settle(ending: AudioPlaybackEnding, message: string | null): void {
    if (currentPlaybackId === null) return;

    const completion: AudioPlaybackCompletion = {
      playbackId: currentPlaybackId,
      ending,
      message,
    };
    const resolve = resolveCurrent;

    currentPlaybackId = null;
    resolveCurrent = null;
    status = toStatusAfter(ending);

    resolve?.(completion);
    for (const listener of listeners) listener(completion);
    notify();
  }

  audio?.addEventListener('ended', () =>
    settle(AUDIO_PLAYBACK_ENDINGS.Completed, null)
  );
  audio?.addEventListener('error', () =>
    settle(AUDIO_PLAYBACK_ENDINGS.Failed, null)
  );

  function play(
    request: AudioPlaybackRequest
  ): Promise<AudioPlaybackCompletion> {
    settle(AUDIO_PLAYBACK_ENDINGS.Superseded, null);

    if (audio === null) {
      return Promise.resolve({
        playbackId: request.playbackId,
        ending: AUDIO_PLAYBACK_ENDINGS.Failed,
        message: AUDIO_ELEMENT_UNAVAILABLE_MESSAGE,
      });
    }

    generation += 1;
    const playbackGeneration = generation;

    audio.src = request.url;
    audio.currentTime = 0;
    currentPlaybackId = request.playbackId;
    status = AUDIO_PLAYBACK_STATUSES.Playing;
    notify();

    return new Promise<AudioPlaybackCompletion>((resolve) => {
      resolveCurrent = resolve;

      audio.play().then(
        () => {
          if (playbackGeneration === generation) request.onStarted?.();
        },
        (error: unknown) => {
          if (playbackGeneration !== generation) return;

          settle(AUDIO_PLAYBACK_ENDINGS.Failed, toFailureMessage(error));
        }
      );
    });
  }

  function stop(playbackId?: string): void {
    if (currentPlaybackId === null) return;
    if (playbackId !== undefined && playbackId !== currentPlaybackId) return;

    audio?.pause();
    settle(AUDIO_PLAYBACK_ENDINGS.Stopped, null);
  }

  function onEnded(listener: PlaybackListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return {
    get status() {
      return status;
    },
    get playbackId() {
      return currentPlaybackId;
    },
    play,
    stop,
    onEnded,
  };
}
